import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import OffCanvas from "../../components/offCanvas";
import { ToasterObject } from "./interface-utils";
import { Toast } from "react-bootstrap";
import { ToasterContext } from "../../contextApi/ToasterContext";
import {
  createAppointmentApi,
} from "../../api-helpers/AppointmentApiHelper";
import {
  AppointmentCancellationReason,
  AppointmentDto,
  AppointmentReminderDTO,
} from "appointment-experience-client";
import AppointmentScheduler from "./AppointmentScheduler";
import AddAppointmentRadio from "./AddAppointmentRadio";
import {
  PatientNotificationQueuesDto,
  WorkflowState,
} from "common-util-experience-client";
import { Constants, Workflow } from "../../constants/Lookups";
import {
  createPatientNotificationApi,
  createWorkflowApi,
} from "../../api-helpers/CommonUtilsApiHelper";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import { PatientPersonalContact } from "patients-experience-client";
import { setPageTitle } from "../../store/slices/pageTitleSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { startAppointment } from "../../store/slices/appointmentOverlaySlice";
import { buildVisitActionTasks } from "../../constants/AppointmentOverlayTasks";
import {
  findAppointmentStatusId,
  loadAppointment,
} from "../../api-helpers/appointmentWorkflow";
import { AppointmentConstants } from "../../constants/Appointment";
import ConfirmationModal from "../../components/ConfirmationModal";
import { ClinicianAiSummaryPanel } from "../../components/ClinicianAiSummaryPanel";
import { isAiSummaryEnabled, isAiSummaryAdminOnly } from "../../constants/FeatureFlags";
import { CareJourneyRoadmap } from "../../components/CareJourneyRoadmap/CareJourneyRoadmap";
import { AppointmentActivityPanel } from "./AppointmentActivityPanel";
import { useRoleAuthorization } from "../../customHooks/useRoleAuthorization";
import { USER_ROLES } from "../../constants/UserRoles";
import { AppointmentHero } from "./appointment-detail/AppointmentHero";
import { AppointmentHistoryCard } from "./appointment-detail/AppointmentHistoryCard";
import { AppointmentNotificationsCard } from "./appointment-detail/AppointmentNotificationsCard";
import { PostVisitSummaryCard } from "./appointment-detail/PostVisitSummaryCard";
import { AppointmentDetailSkeleton } from "./appointment-detail/AppointmentDetailSkeleton";
import { useCareJourney } from "../../hooks/useCareJourney";
import "../../assets/scss/custom/_patientSummaryGrid.scss";
import "../../assets/scss/custom/_appointmentDetail.scss";

/**
 * End states an appointment cannot be worked from. Once in one, the page shows
 * the visit as it happened: read-only hero, its activity and its summary.
 */
const FINAL_APPOINTMENT_STATUSES: string[] = [
  Workflow.APPOINTMENT_STATUS_CANCELED,
  Workflow.APPOINTMENT_STATUS_COMPLETED,
  Workflow.APPOINTMENT_STATUS_NO_SHOW,
];

/** Statuses a reminder can still be resent from. */
const RESENDABLE_APPOINTMENT_STATUSES: string[] = [
  Workflow.APPOINTMENT_STATUS_PLANNED,
  Workflow.APPOINTMENT_STATUS_INPROGRESS,
];

const NOTIFICATION_POLL_INTERVAL_MS = 10000;

/**
 * The page reveals once every section has loaded, so nothing pops in piece by
 * piece. A slow section gets this long before the page shows anyway, with that
 * section showing its own placeholder.
 */
const REVEAL_TIMEOUT_MS = 3000;

/**
 * The patient's own contact numbers, most-preferred first (primary, secondary, other).
 * Shared by the appointment header and the appointment overlay so both agree on which
 * number is "the" number to call.
 */
const selfContactsByPreference = (
  contacts?: PatientPersonalContact[]
): PatientPersonalContact[] => {
  const preferenceOrder: Record<string, number> = { p: 1, s: 2, o: 3 };
  return (
    (contacts ?? [])
      .filter((contact) => contact.contactType.name.toLowerCase() === "self")
      // Preferences arrive as "P"/"S"/"O"; compare case-insensitively.
      .sort(
        (a, b) =>
          (preferenceOrder[a.contactPreference?.toLowerCase()] || 4) -
          (preferenceOrder[b.contactPreference?.toLowerCase()] || 4)
      )
  );
};

/** Oldest first, by when each notification went (or will go) out. */
const sortNotifications = (notifications: PatientNotificationQueuesDto[]) =>
  notifications.sort((a, b) => {
    const dateA = new Date(a.deliveredAt ?? a.scheduledAt ?? a.createdAt ?? 0).getTime();
    const dateB = new Date(b.deliveredAt ?? b.scheduledAt ?? b.createdAt ?? 0).getTime();
    return dateA - dateB;
  });

/**
 * Keyed so the page starts fresh, rather than showing stale data, when:
 * - moving to another appointment from the history (same route, new id), and
 * - the overlay takes this appointment or lets it go. Exiting the overlay puts
 *   the appointment back to Planned and finishing it ends it, then returns to
 *   this same route, so without the reload the page would still say In Progress
 *   (and hide Cancel).
 */
export const ScheduleDetails = () => {
  const { appointmentId } = useParams();
  const isInOverlay = useSelector(
    (state: RootState) =>
      !!appointmentId && state.appointmentOverlay.appointmentId === appointmentId
  );
  return (
    <AppointmentDetailPage key={`${appointmentId}:${isInOverlay ? "live" : "idle"}`} />
  );
};

const AppointmentDetailPage = () => {
  const workflowApi = createWorkflowApi();
  const appointmentApi = createAppointmentApi();
  const patientApi = createPatientApi();
  const notificationApi = createPatientNotificationApi();
  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [openToaster, setOpenToaster] = useState<ToasterObject>({
    message: "",
    show: false,
    type: "",
  });
  const [isEmailResendEnabled, setIsEmailResendEnabled] = useState<boolean>(true);
  const [isSMSResendEnabled, setIsSMSResendEnabled] = useState<boolean>(true);
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDto | null>(
    null
  );
  const [appointmentNotificationDetails, setAppointmentNotificationDetails] = useState<
    PatientNotificationQueuesDto[] | null
  >(null);
  const location = useLocation();
  // Rendered under the patient profile: /patients/:patientId/appointments/:appointmentId
  const { appointmentId, patientId: routePatientId } = useParams();
  // The profile hands its patient down; undefined when rendered on its own.
  const navigate = useNavigate();
  // Appointment actions return to the patient's chart appointments tab; falls back to the schedule until details load.
  const patientId = routePatientId ?? appointmentDetails?.patients[0]?.patientId;
  const returnPath: string = patientId
    ? `/patients/${patientId}/appointments`
    : "/schedule";
  const toasterContext = useContext(ToasterContext);
  const [patientContactInfo, setPatientContactInfo] =
    useState<PatientPersonalContact[]>();
  const [modalShow, setModalShow] = useState(false);
  const [states, setStates] = useState<WorkflowState[]>([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [cancellationReasons, setCancellationReasons] = useState<
    AppointmentCancellationReason[]
  >([]);
  const [appointmentLoadFailed, setAppointmentLoadFailed] = useState(false);
  const [contactsSettled, setContactsSettled] = useState(false);
  const [historySettled, setHistorySettled] = useState(false);
  const [activitySettled, setActivitySettled] = useState(false);
  const [revealTimedOut, setRevealTimedOut] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const markHistorySettled = useCallback(() => setHistorySettled(true), []);
  const markActivitySettled = useCallback(() => setActivitySettled(true), []);

  const dispatch = useDispatch();
  const isAdministrator = useRoleAuthorization([USER_ROLES.ADMINISTRATOR]);
  const canViewAiSummary = isAiSummaryAdminOnly() ? isAdministrator : true;

  const statusName = appointmentDetails?.status?.stateName ?? "";
  // Nothing can be worked from an end state, so the page switches to the
  // post-visit view and the actions that change the appointment hide.
  const isAppointmentInFinalState = FINAL_APPOINTMENT_STATUSES.includes(statusName);
  const isAppointmentInProgress = statusName === Workflow.APPOINTMENT_STATUS_INPROGRESS;
  const isVideoCall = appointmentDetails?.appointmentMode?.name === Constants.VIDEO_MODE;
  const appointmentPatientId = appointmentDetails?.patients[0]?.patientId;

  // Appointment experience overlay (WCP-13240). Only one appointment can be in
  // progress at a time: while this one is in the widget the hero shows it's live
  // (it's run and ended from there), and Start is disabled while another is open.
  const overlayAppointmentId = useSelector(
    (state: RootState) => state.appointmentOverlay.appointmentId
  );
  const isThisAppointmentInOverlay =
    !!appointmentDetails?.id && overlayAppointmentId === appointmentDetails.id;
  const isOtherAppointmentInOverlay =
    !!overlayAppointmentId && !isThisAppointmentInOverlay;
  const [isStartingAppointment, setIsStartingAppointment] = useState(false);

  // The number the overlay offers for a phone visit. The page loads contacts in
  // the background, so if Start is clicked before they arrive, fetch them here
  // rather than starting the visit with no number. A failed lookup doesn't
  // block the start.
  const resolveVisitPhone = async (patientId: string): Promise<string | null> => {
    try {
      const contacts =
        patientContactInfo ??
        (await patientApi.patientsControllerGetContacts(patientId)).data;
      return selfContactsByPreference(contacts)[0]?.phone ?? null;
    } catch (error) {
      console.error("Error loading the patient's phone number:", error);
      return null;
    }
  };

  const handleStartAppointment = async () => {
    const patient = appointmentDetails?.patients?.[0];
    if (!appointmentDetails?.id || !patient?.patientId) return;

    const isVideoVisit =
      appointmentDetails.appointmentMode?.name === Constants.VIDEO_MODE;

    // Move the appointment to In-Progress before showing the overlay, so the widget
    // never claims an appointment is running that the backend did not accept.
    setIsStartingAppointment(true);
    try {
      const appointment = await loadAppointment(appointmentDetails.id);
      const inProgressId = findAppointmentStatusId(
        appointment,
        Workflow.APPOINTMENT_STATUS_INPROGRESS
      );

      if (!inProgressId) {
        throw new Error(
          `Appointment cannot move to ${Workflow.APPOINTMENT_STATUS_INPROGRESS} from ${appointment.status?.stateName}`
        );
      }

      await appointmentApi.appointmentControllerUpdateAppointmentById(
        { status: inProgressId },
        appointmentDetails.id
      );
    } catch (error) {
      console.error("Error starting the appointment:", error);
      setOpenToaster({
        message: "Could not start the appointment. Please try again.",
        show: true,
        type: "error",
      });
      return;
    } finally {
      setIsStartingAppointment(false);
    }

    const phone = isVideoVisit ? null : await resolveVisitPhone(patient.patientId);

    dispatch(
      startAppointment({
        appointmentId: appointmentDetails.id,
        patientId: patient.patientId,
        patientName: patient.patientName,
        detailsPath: location.pathname,
        scheduledMinutes: appointmentDetails.durationMinutes ?? null,
        connectivity: {
          mode: appointmentDetails.appointmentMode?.name ?? "",
          videoProvider: isVideoVisit ? (appointmentDetails.videoProvider ?? null) : null,
          phone,
        },
        // The clinician ticks this off; the journey's rows are computed.
        tasks: buildVisitActionTasks(patient.patientId),
      })
    );

    // Starting a visit puts the clinician in front of the patient, so land on the
    // profile. Refreshing this page's details first would be wasted work -- the
    // overlay carries the appointment from here, and its "Appointment details"
    // button comes back to this route.
    navigate(`/patients/${patient.patientId}`);
  };

  const loadStates = async () => {
    try {
      const response =
        await workflowApi.workflowControllerGetStatesForWorkflow("Appointment");
      setStates(response.data);
    } catch (error) {
      console.error("Failed to load states:", error);
    }
  };

  const getStatusId = async (statusName: string): Promise<string> => {
    const appointmentdetails =
      await appointmentApi.appointmentControllerGetAppointmentById(
        appointmentId!
      );
    const appointment: AppointmentDto = appointmentdetails.data;
    if (appointment.status.stateName === statusName) {
      return appointment.status.stateId;
    }
    const noShowState = appointment.status.nextStates.find(
      (state: WorkflowState) =>
        state.stateName?.toLowerCase() === statusName?.toLowerCase()
    );

    return noShowState.stateId;
  };

  const getWfState = (stateName: string): WorkflowState | undefined => {
    const state = states.find((state: WorkflowState) => state.stateName === stateName);
    return state;
  };

  const handleCancelAppointment = async () => {
    const statusId = await getStatusId(Workflow.APPOINTMENT_STATUS_CANCELED);
    await appointmentApi.appointmentControllerUpdateAppointmentById(
      { status: statusId!, cancelledReason: selectedReason },
      appointmentId!
    );

    if (!toasterContext) {
      throw new Error("ToasterContext is not available");
    }
    const { showToast } = toasterContext;
    setModalShow(false);
    showToast({
      type: "success",
      title: "Appointment canceled successfully",
      icon: "check_circle",
    });
    navigate(returnPath);
  };

  // Everything the route already identifies loads at once, in parallel with
  // the appointment, rather than waiting for it.
  useEffect(() => {
    dispatch(setPageTitle("1:1 Appointment | Spire360"));
    void getAppointmentDetailsById();
    void getAppointmentNotificationDetails();
    void loadStates();
    void getAppointmentCancellationReasons();
  }, []);

  // Under the patient profile the route names the patient; the legacy URL only
  // knows it once the appointment has loaded.
  useEffect(() => {
    if (patientId) void getPatientContactInfo(patientId);
  }, [patientId]);

  const getPatientContactInfo = async (patientId: string) => {
    try {
      const response = await patientApi.patientsControllerGetContacts(patientId);
      setPatientContactInfo(response.data);
    } catch (error) {
      console.error("Error loading the patient's contacts:", error);
    } finally {
      setContactsSettled(true);
    }
  };

  const getAppointmentDetailsById = async () => {
    try {
      const appointmentDetail =
        await appointmentApi.appointmentControllerGetAppointmentById(
          appointmentId!
        );
      setAppointmentDetails(appointmentDetail.data);
    } catch (err) {
      console.error("Error loading the appointment:", err);
      setAppointmentLoadFailed(true);
    }
  };

  const pollNotificationList = (remainderType: string) => {
    const setResendEnabled =
      remainderType === AppointmentConstants.SMS
        ? setIsSMSResendEnabled
        : setIsEmailResendEnabled;
    setResendEnabled(false);

    const checkNotifications = async () => {
      try {
        const response =
          await notificationApi.patientNotificationsControllerGetNotificationDetailsByReferenceId(
            appointmentId!
          );
        const sortedNotifications = sortNotifications(response.data);
        setAppointmentNotificationDetails(sortedNotifications);

        const hasScheduled = sortedNotifications.some(
          (n) =>
            n.remainderType === remainderType &&
            n.referenceType === AppointmentConstants.APPOINTMENT_REMINDER_NOTIFICATION &&
            n.status === AppointmentConstants.REMINDER_STATUS_SCHEDULED
        );
        if (!hasScheduled) {
          clearInterval(intervalId);
          setResendEnabled(true);
        }
      } catch (error) {
        console.error(`Error polling notification status:`, error);
        clearInterval(intervalId);
        setResendEnabled(true);
      }
    };
    const intervalId = setInterval(
      () => void checkNotifications(),
      NOTIFICATION_POLL_INTERVAL_MS
    );
  };

  const handleResendReminder = async (remainderType: string) => {
    const coachId = appointmentDetails?.coaches[0]?.coachId;
    const reminderPatientId = appointmentDetails?.patients[0]?.patientId;
    if (!appointmentDetails || !coachId || !reminderPatientId) return;
    try {
      if (remainderType === AppointmentConstants.EMAIL) {
        setIsEmailResendEnabled(false);
      }
      if (remainderType === AppointmentConstants.SMS) {
        setIsSMSResendEnabled(false);
      }
      const reminderBody: AppointmentReminderDTO = {
        coachId,
        patientId: reminderPatientId,
        appointmentId: appointmentDetails.id,
        reminderType: remainderType,
      };
      // Axios rejects on a non-2xx response, so reaching here means it was accepted.
      await appointmentApi.appointmentControllerSendAppointmentReminder(reminderBody);
      pollNotificationList(remainderType);
      setOpenToaster({
        message: `Appointment reminder sent successfully!`,
        show: true,
        type: "success",
      });
    } catch (error) {
      if (remainderType === AppointmentConstants.EMAIL) {
        setIsEmailResendEnabled(true);
      }
      if (remainderType === AppointmentConstants.SMS) {
        setIsSMSResendEnabled(true);
      }
      setOpenToaster({
        message: `Unable to send appointment reminder to patient!`,
        show: true,
        type: "error",
      });
    }
  };

  const getAppointmentNotificationDetails = async () => {
    try {
      const appointmentNotificationDetail =
        await notificationApi.patientNotificationsControllerGetNotificationDetailsByReferenceId(
          appointmentId!
        );
      setAppointmentNotificationDetails(
        sortNotifications(appointmentNotificationDetail.data)
      );
    } catch (err) {
      console.error("Error loading the appointment's notifications:", err);
      setAppointmentNotificationDetails([]);
    }
  };

  useEffect(() => {
    const interval = setTimeout(() => {
      setOpenToaster({ ...openToaster, show: false });
    }, 3000);
    return () => clearTimeout(interval);
  }, [openToaster]);

  const handleCopyClick = (webinarUrl: string) => {
    void navigator.clipboard.writeText(webinarUrl);
    setOpenToaster({
      message: `Link copied to clipboard`,
      show: true,
      type: "success",
    });
  };

  const getAppointmentCancellationReasons = async () => {
    const reasons =
      await appointmentApi.appointmentControllerGetAppointmentCancellationReasons();
    setCancellationReasons(reasons.data);
  };

  // The journey loads with the page and is handed to the roadmap, so it can
  // count towards the reveal instead of expanding afterwards.
  const journey = useCareJourney(patientId);
  const rpmOnly = useSelector((state: RootState) =>
    patientId ? state.patientRpmOnly.byPatientId[patientId] : undefined
  );
  const showsJourney = !isAppointmentInFinalState && rpmOnly === false;

  const isPageReady =
    appointmentDetails !== null &&
    contactsSettled &&
    appointmentNotificationDetails !== null &&
    (!appointmentPatientId ||
      (isAppointmentInFinalState
        ? activitySettled
        : historySettled && (!showsJourney || journey.status !== "loading")));

  // Once shown, the page stays shown: later reloads (a journey refresh, a
  // resent reminder) update in place.
  useEffect(() => {
    if (isPageReady || revealTimedOut || appointmentLoadFailed) setRevealed(true);
  }, [isPageReady, revealTimedOut, appointmentLoadFailed]);

  useEffect(() => {
    if (!appointmentDetails) return;
    const timer = setTimeout(() => setRevealTimedOut(true), REVEAL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [appointmentDetails !== null]);

  const renderSummary = () => {
    if (!appointmentDetails || !appointmentPatientId) return null;
    if (isAppointmentInFinalState) {
      return (
        <PostVisitSummaryCard
          appointmentId={appointmentDetails.id}
          isVideoCall={isVideoCall}
        />
      );
    }
    if (!isAiSummaryEnabled() || !canViewAiSummary) return null;
    return (
      <section className="appointment-card">
        <div className="appointment-card__header">
          <h3 className="appointment-card__title">AI Patient Summary</h3>
        </div>
        <ClinicianAiSummaryPanel patientId={appointmentPatientId} />
      </section>
    );
  };

  return (
    <>
      <div className="col-3">
        <Toast
          className={`toast toast-${
            openToaster.type === "success"
              ? "success"
              : openToaster.type === "error"
                ? "danger"
                : ""
          } top-right mt-10 move-in`}
          show={openToaster.show}
          onClose={() => setOpenToaster({ message: "", show: false, type: "" })}
        >
          <div className="ps-3">
            <span className="material-symbols-outlined fs-36">
              {openToaster.type === "success"
                ? "check_circle_outline"
                : openToaster.type === "error"
                  ? "error_outline"
                  : ""}
            </span>
          </div>
          <Toast.Body className="flex-fill">
            <div className="fs-14">
              {openToaster.type === "success"
                ? `${openToaster.message}`
                : openToaster.type === "error"
                  ? `${openToaster.message}`
                  : ""}
            </div>
          </Toast.Body>
          <Toast.Header></Toast.Header>
        </Toast>
      </div>
      {/* Reschedule is temporarily unavailable; its panel stays wired for when it returns. */}
      <OffCanvas
        title={"Reschedule Appointment"}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
        offCanvasComp={offCanvasComp}
        placement={offCanvasComp === "rescheduleV2" ? "top" : "end"}
        className={offCanvasComp === "eventCalendar" ? "w-95" : ""}
        backdrop={offCanvasComp === "rescheduleV2" ? "static" : true}
      >
        {offCanvasComp === "rescheduleV2" ? (
          appointmentDetails && (
            <AppointmentScheduler
              setOpenToaster={setOpenToaster}
              setOffCanvasShow={setOffCanvasShow}
              setOffCanvasComp={setOffCanvasComp}
              appointmentId={appointmentDetails.id}
              patientId={appointmentDetails.patients[0]?.patientId}
              loadAppointmentDetails={() => void getAppointmentDetailsById()}
              rescheduleStateId={
                getWfState(Workflow.APPOINTMENT_STATUS_RESCHEDULED)?.stateId
              }
              initialTypeId={appointmentDetails.appointmentType?.id ?? undefined}
              initialModeId={appointmentDetails.appointmentMode?.id ?? undefined}
              initialCoachId={appointmentDetails.coaches[0]?.coachId}
            />
          )
        ) : (
          <AddAppointmentRadio
            setOpenToaster={setOpenToaster}
            offCanvasShow={offCanvasShow}
            offCanvasComp={offCanvasComp}
            setOffCanvasShow={setOffCanvasShow}
            setOffCanvasComp={setOffCanvasComp}
            appointmentId={appointmentDetails?.id}
            loadAppointmentDetails={() => void getAppointmentDetailsById()}
          />
        )}
      </OffCanvas>
      {/* Embedded in the patient profile, under its header and tabs. */}
      <div className="appointment-detail patient-appointment">
        <Link to={returnPath} className="appointment-detail__back">
          <span
            className="appointment-detail__back-icon material-symbols-outlined"
            aria-hidden="true"
          >
            chevron_left
          </span>
          <span>All appointments</span>
        </Link>

        {appointmentLoadFailed && (
          <section className="appointment-card">
            <span className="appointment-card__empty">
              Unable to load this appointment. Please refresh to try again.
            </span>
          </section>
        )}

        {!revealed && !appointmentLoadFailed && (
          <AppointmentDetailSkeleton showJourney={showsJourney} />
        )}

        {/* Mounted (and loading) behind the skeleton, then shown all at once. */}
        {appointmentDetails && (
          <div className="appointment-detail__content" hidden={!revealed}>
            {/* See .appointment-detail__layout: the sidebar sits beside the summary, and
                on wide screens notifications move up beside the hero. */}
            <div className="appointment-detail__layout">
              <div className="appointment-detail__main">
                <div className="appointment-detail__top">
                  <AppointmentHero
                    appointment={appointmentDetails}
                    phones={selfContactsByPreference(patientContactInfo).map(
                      (contact) => contact.phone
                    )}
                    isReadOnly={isAppointmentInFinalState}
                    // A visit already under way is ended from the overlay -- as a no
                    // show or as completed -- rather than cancelled.
                    canCancel={!isAppointmentInProgress}
                    isInOverlay={isThisAppointmentInOverlay}
                    isOtherInOverlay={isOtherAppointmentInOverlay}
                    isStarting={isStartingAppointment}
                    onStart={() => void handleStartAppointment()}
                    onCancel={() => setModalShow(true)}
                    onCopyLink={handleCopyClick}
                  />

                  {appointmentPatientId &&
                    (isAppointmentInFinalState ? (
                      <section className="appointment-card">
                        <div className="appointment-card__header">
                          <h3 className="appointment-card__title">
                            Appointment Activity
                          </h3>
                        </div>
                        <AppointmentActivityPanel
                          appointmentId={appointmentDetails.id}
                          onSettled={markActivitySettled}
                        />
                      </section>
                    ) : (
                      <AppointmentHistoryCard
                        patientId={appointmentPatientId}
                        currentAppointmentId={appointmentDetails.id}
                        onSettled={markHistorySettled}
                      />
                    ))}
                </div>

                {/* The journey guides the visit, so it's gone once the visit is over. */}
                {appointmentPatientId && !isAppointmentInFinalState && (
                  <div className="appointment-detail__journey">
                    <CareJourneyRoadmap
                      patientId={appointmentPatientId}
                      title="Care Journey"
                      journey={journey}
                    />
                  </div>
                )}

                <div className="appointment-detail__summary">{renderSummary()}</div>
              </div>
              <div className="appointment-detail__sidebar">
                <AppointmentNotificationsCard
                  notifications={appointmentNotificationDetails}
                  canResend={RESENDABLE_APPOINTMENT_STATUSES.includes(statusName)}
                  isEmailResendEnabled={isEmailResendEnabled}
                  isSMSResendEnabled={isSMSResendEnabled}
                  onResend={(reminderType) => void handleResendReminder(reminderType)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modal for cancelling appointment */}
      <ConfirmationModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onConfirm={() => void handleCancelAppointment()}
        title="Confirm Cancellation"
        subtitle="Please select the reason for cancellation below."
        dropdownTitle="Cancellation Reasons"
        showDropdown={true}
        dropdownOptions={cancellationReasons}
        selectedReason={selectedReason}
        onReasonSelect={setSelectedReason}
      />
    </>
  );
};
